import MarkedButton from '@components/Home/MarkedButton'
import Breadcrumb from '@components/shared/Breadcrumb/Breadcrumb'
import FavoriteButton from '@components/shared/FavoriteButton'
import { useUserData } from '@contexts/UserDataContext'
import { getNativeReaderHref } from 'helpers/nativeReaderHref'
import { DefaultSeo } from 'next-seo'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useRef } from 'react'
import { FaBookOpen, FaChevronRight } from 'react-icons/fa6'
import { getCevsenRouteForBab } from 'utils/getCevsenLocalData'

export const cevsenPageBabMapping = [
  {
    page: 1,
    name: '1-10. Bâb arası',
  },
  {
    page: 2,
    name: '11-20. Bâb arası',
  },
  {
    page: 3,
    name: '21-30. Bâb arası',
  },
  {
    page: 4,
    name: '31-40. Bâb arası',
  },
  {
    page: 5,
    name: '41-50. Bâb arası',
  },
  {
    page: 6,
    name: '51-60. Bâb arası',
  },
  {
    page: 7,
    name: '61-70. Bâb arası',
  },
  {
    page: 8,
    name: '71-80. Bâb arası',
  },
  {
    page: 9,
    name: '81-90. Bâb arası',
  },

  {
    page: 10,
    name: '91-100. Bâb arası',
  },
]

/** Shows Cevşen chapter cards and routes readers to their selected or saved bâb. */
const Cevsen = () => {
  const router = useRouter()
  const { userData } = useUserData()
  const pageInputRef = useRef<HTMLInputElement>(null)

  const breadcrumbItems = [
    { label: 'Dualar', href: '/dualar' },
    { label: 'Cevşen-i Şerif' },
  ]

  /** Opens the grouped reader at the requested bâb anchor. */
  const onSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault()
    const bab = Number(pageInputRef.current?.value)
    const route = getCevsenRouteForBab(bab)
    if (route) {
      router.push(getNativeReaderHref(route))
    }
  }

  // render intervals by 10, 1-10, 11-20 etc.

  return (
    <>
      <DefaultSeo
        title="Cevşen-i Şerif Duası Arapça ve Türkçe Meal ile Oku | Hayırhah"
        description="Cevşen duasını uygulamamızda Türkçe ve Arapça Okunuşu ve Anlamı ile
          birlikte okuyabilirsiniz. Duaları okurken kaldığınız sayfayı işaretleyip tekrar
          açtığınızda kaldığınız sayfadan devam edebilirsiniz."
      />
      <div className="mb-2 flex items-center justify-between">
        <Breadcrumb items={breadcrumbItems} />
        <FavoriteButton url="cevsen" size="lg" />
      </div>
      <div className="text-center">
        <div className="mb-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm dark:border-[#1e3445]/60 dark:bg-[#0f1d28]">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-teal-200 bg-teal-50 text-teal-600 dark:border-teal-500/20 dark:bg-teal-900/40 dark:text-teal-400">
            <FaBookOpen aria-hidden="true" className="text-xl" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">
              Cevşen-i Şerif Duası
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              10 sayfa · 100 bâb ve hatim duası
            </p>
          </div>
        </div>
        <p className="mb-4 text-left">
          Cevşen sayfalarını okumak için aşağıdaki butonlara tıklayabilirsiniz.{' '}
        </p>
        <p className="mb-4 text-left">
          Cevşen ve benzeri duaları uygulamamızda Türkçe ve Arapça Okunuşu ve Anlamı ile
          birlikte okuyabilirsiniz. Duaları okurken kaldığınız sayfayı işaretleyip tekrar
          açtığınızda kaldığınız sayfadan devam edebilirsiniz.
        </p>

        <div className="mb-4 text-left">
          <p className="mb-4 font-bold text-left ">
            Cevşen-i Şerif Duasının önemini ve faziletini aşağıdaki videomuzu izleyerek
            öğrenebilirsiniz.
          </p>
          <div className="flex justify-center">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/aj-FYhfEEfc?si=aUiTreDAgSKA-M_i"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
        <div>
          <Link
            href={getNativeReaderHref('/dualar/cevsen-i-serif')}
            className="block w-full mb-4 tracking-wider underline"
          >
            Her Sayfada 1 Bab olan Tekli Cevşen-i Şerif Duası İçin Buraya Tıklayın
          </Link>
        </div>
        <div className="m-auto mb-4 max-w-[700px] rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-[#1e3445]/60 dark:bg-[#13222e]">
          <h3 className="mb-4 text-xl font-medium text-center">Kalınan Bâb</h3>
          <MarkedButton
            type="cevsen"
            page={userData.cevsen}
            lastReadPage={userData.lastReadPages?.cevsen}
          />
        </div>
        <div className="m-auto mb-4 max-w-[700px] rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-[#1e3445]/60 dark:bg-[#13222e]">
          <h3 className="mb-4 text-xl font-medium text-center">Bâb'a Git</h3>
          <form onSubmit={onSubmitHandler} className="flex">
            <input
              className="input-field mr-2 "
              type="number"
              required
              max={101}
              min={1}
              name="page"
              placeholder="Gitmek istediginiz bâb'ı girin..."
              id="page"
              aria-label="Bâb numarası"
              ref={pageInputRef}
            />
            <button
              className="w-1/4 rounded-full bg-teal-600 font-semibold text-white hover:bg-teal-500"
              type="submit"
            >
              Git
            </button>
          </form>
        </div>
        <nav aria-label="Cevşen sayfaları" className="grid gap-3 sm:grid-cols-2">
          {cevsenPageBabMapping.map((item) => (
            <Link
              key={item.page}
              href={getNativeReaderHref(`/cevsen/${item.page}`)}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-teal-500 dark:border-[#1e3445]/60 dark:bg-[#13222e] dark:hover:border-teal-500"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-50 font-bold text-teal-600 dark:bg-[#0c3947] dark:text-teal-400">
                {item.page}
              </span>
              <span className="flex-1 font-medium text-slate-800 dark:text-slate-200">
                {item.name}
              </span>
              <FaChevronRight
                aria-hidden="true"
                className="text-xs text-teal-600 dark:text-teal-400"
              />
            </Link>
          ))}
        </nav>
      </div>
    </>
  )
}

export default Cevsen
